import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';

import { BsGearWideConnected } from 'react-icons/bs';
import { FaSignOutAlt } from 'react-icons/fa';
import { HiOutlineMenu, HiX, HiChevronDown } from 'react-icons/hi';
import { FcDocument, FcList, FcPieChart } from 'react-icons/fc';

import { useAuthContext } from 'Hooks';
import { formatRole, canAskRequest, canCreateUser } from 'Helpers';

import icon from 'Assets/tempoIconWhite.png';

const getDrawerSize = ({
  isOpen,
  isMobile,
  isPage,
}: {
  isOpen: boolean;
  isMobile?: boolean;
  isPage?: boolean;
}) => {
  if (isMobile) {
    if (isOpen && isPage) return '0';
    if (isOpen && !isPage) return '100%';
    if (!isOpen) return '0';
  } else {
    if (isOpen) return '15.5rem';
    else return '5rem';
  }
};

type RoutesProps = {
  name: string;
  path: string;
  icon: JSX.Element;
  subRoutes?: { name: string; path: string; hasAccess: boolean }[];
  hasAccess: boolean;
};

const SubMenu = ({
  isOpen,
  pathname,
  item,
}: {
  isOpen: boolean;
  pathname: string;
  item: RoutesProps;
}): JSX.Element => {
  const [dropdown, setDropdown] = useState(false);

  useEffect(() => {
    if (pathname.startsWith(item.path)) setDropdown(true);
  }, [pathname]);

  return (
    <NavItem
      $hasAccess={item.hasAccess}
      onClick={() => {
        setDropdown(!dropdown);
      }}
    >
      <ItemWrapper $isActive={pathname.startsWith(item.path)}>
        <Item>
          <ItemIcon>{item.icon}</ItemIcon>
          {isOpen && <ItemHeading>{item.name}</ItemHeading>}
        </Item>
        {isOpen && (
          <ItemIcon $dropdown={true} $rotate={dropdown}>
            <HiChevronDown />
          </ItemIcon>
        )}
      </ItemWrapper>

      {dropdown && (
        <SubMenuWrapper>
          {item.subRoutes?.map(({ name, path, hasAccess }, index) => (
            <SubNavLink
              key={index}
              to={path}
              $isActive={pathname === path}
              $hasAccess={hasAccess}
            >
              {name}
            </SubNavLink>
          ))}
        </SubMenuWrapper>
      )}
    </NavItem>
  );
};

export const Drawer = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { pathname } = useLocation();
  const role = formatRole({ role: user?.role || '' });
  const [isOpen, setIsOpen] = useState(false);

  const handleDisconnect = () => {
    window.location.replace('https://connect.tempo-one.com/');
  };

  const Routes: RoutesProps[] = [
    {
      name: t('Navbar.Dashboard'),
      path: '/',
      icon: <FcPieChart />,
      hasAccess: true,
    },
    {
      name: t('Navbar.Requests'),
      path: '/requests/',
      icon: <FcList />,
      hasAccess: true,
    },
    {
      name: t('Navbar.Request'),
      path: '/request/',
      subRoutes: [
        {
          name: t('Navbar.Charter'),
          path: '/request/charter',
          hasAccess: true,
        },
        {
          name: t('Navbar.Logistic'),
          path: '/request/logistic',
          hasAccess: canAskRequest({ user }),
        },
        {
          name: t('Navbar.Overseas'),
          path: '/request/overseas',
          hasAccess: canAskRequest({ user }),
        },
        {
          name: t('Navbar.Road'),
          path: '/request/road',
          hasAccess: canAskRequest({ user }),
        },
      ],
      icon: <FcDocument />,
      hasAccess: true,
    },
    {
      name: t('Navbar.Settings'),
      path: '/factory',
      subRoutes: [
        {
          name: t('Navbar.Clients'),
          path: '/factory/clients',
          hasAccess: true,
        },
        {
          name: t('Navbar.Prospects'),
          path: '/factory/prospects',
          hasAccess: true,
        },
        {
          name: t('Navbar.Accounts'),
          path: '/factory/accounts',
          hasAccess: true,
        },
        { name: t('Navbar.Users'), path: '/factory/users', hasAccess: true },
      ],
      icon: <BsGearWideConnected />,
      hasAccess: canCreateUser({ user, label: 'CLIENT' }),
    },
  ];

  return (
    <>
      <Hamburger $isOpen={isOpen} onClick={() => setIsOpen(true)}>
        <HiOutlineMenu />
      </Hamburger>
      <DrawerContainer
        $isOpen={isOpen}
        onMouseLeave={() => setIsOpen(false)}
        onClick={() => setIsOpen(true)}
      >
        <DrawerWrapper>
          <UserWrapper>
            <Close onClick={() => setIsOpen(false)}>
              <HiX />
            </Close>
            <Img src={icon} />
            {isOpen && (
              <UserInfo>
                <Name>{user?.fullName}</Name>
                <Name $header>{role}</Name>
              </UserInfo>
            )}
          </UserWrapper>

          {user?.isActive && (
            <NavigationWrapper>
              {Routes.map((item, index) =>
                item.subRoutes ? (
                  <SubMenu
                    key={index}
                    isOpen={isOpen}
                    pathname={pathname}
                    item={item}
                  />
                ) : (
                  <NavLink key={index} to={item.path}>
                    <ItemWrapper $isActive={pathname === item.path}>
                      <Item>
                        <ItemIcon>{item.icon}</ItemIcon>
                        {isOpen && <ItemHeading>{item.name}</ItemHeading>}
                      </Item>
                    </ItemWrapper>
                  </NavLink>
                ),
              )}
            </NavigationWrapper>
          )}

          <LogoutWrapper>
            <ItemWrapper onClick={handleDisconnect}>
              <Item>
                <ItemIcon>
                  <FaSignOutAlt />
                </ItemIcon>
                {isOpen && <ItemHeading>Déconnexion</ItemHeading>}
              </Item>
            </ItemWrapper>
          </LogoutWrapper>
        </DrawerWrapper>
      </DrawerContainer>
      <PageContainer $isOpen={isOpen}>{children}</PageContainer>
    </>
  );
};

const Hamburger = styled.div<{ $isOpen: boolean }>`
  display: none;
  position: absolute;
  cursor: pointer;
  top: 0.3rem;
  right: 1rem;
  margin-bottom: 1rem;
  border-radius: 5px;
  z-index: 99;
  box-shadow: rgba(21, 64, 119, 0.2) 0px 2px 8px;
  & > svg {
    color: #154077;
    font-size: 1.9rem;
  }

  @media (max-width: 960px) {
    display: ${({ $isOpen }) => ($isOpen ? 'none' : 'block')};
  }
`;

const Close = styled.div`
  display: none;
  position: absolute;
  cursor: pointer;
  top: 0.2rem;
  right: 0.5rem;
  & > svg {
    color: white;
    font-size: 2rem;
  }
  @media (max-width: 960px) {
    display: block;
  }
`;

const PageContainer = styled.div<{ $isOpen: boolean }>`
  padding-left: ${({ $isOpen }) =>
    getDrawerSize({ isOpen: $isOpen, isPage: true })};
  transition: padding-left 0.5s cubic-bezier(0.38, 0.01, 0.09, 0.98);

  @media (max-width: 960px) {
    padding-left: ${({ $isOpen }) =>
      getDrawerSize({ isOpen: $isOpen, isMobile: true, isPage: true })};
  }
`;

const DrawerContainer = styled.nav<{ $isOpen: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${({ $isOpen }) => getDrawerSize({ isOpen: $isOpen })};
  background: #154077;
  transition: width 0.5s cubic-bezier(0.38, 0.01, 0.09, 0.98);
  border-radius: 0 0.45rem 0.45rem 0;
  z-index: 99;

  @media (max-width: 960px) {
    width: ${({ $isOpen }) =>
      getDrawerSize({ isOpen: $isOpen, isMobile: true })};
  }
`;

const DrawerWrapper = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  overflow: hidden;
  height: 100%;

  & > div:nth-child(2) {
    margin: 1.5rem 0;
  }

  & > div {
    padding: 0 1.2rem;
  }
`;

const LogoutWrapper = styled.div`
  width: 100%;
  border: none;
  cursor: pointer;
  position: absolute;
  bottom: 1rem;
`;

const NavigationWrapper = styled.div`
  position: relative;
`;

const NavLink = styled(Link)`
  width: 100%;
  cursor: pointer;
  position: relative;
  padding: 0.8rem 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  font-weight: 400;
`;

const NavItem = styled.div<{ $hasAccess: boolean }>`
  width: 100%;
  cursor: pointer;
  position: relative;
  padding: 0.8rem 0;
  display: ${({ $hasAccess }) => ($hasAccess ? 'flex' : 'none')};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  font-weight: 400;
  user-select: none;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
`;

const ItemWrapper = styled.div<{ $isActive?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ $isActive }) =>
    $isActive ? 'white !important' : 'transparent'};
  color: ${({ $isActive }) => ($isActive ? '#154077' : 'white')};
  transition: 0.15s ease-in-out;
  border-radius: 0.5rem;
  user-select: none;
  &:hover {
    background-color: #1b539c;
  }
  ${({ onClick }) =>
    onClick !== undefined &&
    css`
      cursor: pointer;
    `}
`;

const ItemIcon = styled.span<{
  $dropdown?: boolean;
  $rotate?: boolean;
}>`
  ${({ $dropdown, $rotate }) =>
    $dropdown &&
    css`
      rotate: ${$rotate ? '0' : '-90deg'};
    `}
  padding: ${({ $dropdown }) => ($dropdown ? '0' : '0.4rem 0.5rem')};
  border: none;
  cursor: pointer;
  transition: rotate 0.3s ease-in-out;
  font-size: 1.6rem;
`;

const ItemHeading = styled.span<{ isLock?: boolean }>`
  font-size: 1.1rem;
  font-weight: bolder;
  white-space: nowrap;
  overflow: hidden;
`;

const SubMenuWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0rem 0 0 1.25rem;
`;

const SubNavLink = styled(Link)<{ $isActive: boolean; $hasAccess: boolean }>`
  display: ${({ $hasAccess }) => ($hasAccess ? 'flex' : 'none')};
  width: 100%;
  border-left: 3px solid
    ${({ $isActive }) => ($isActive ? '#aabb00' : '#194d90')};
  padding: 0.4rem 0rem 0.4rem 1.5rem;
  font-size: 1rem;
  color: white;
  font-weight: ${({ $isActive }) => ($isActive ? '600' : '500')};
  white-space: nowrap;
  overflow: hidden;
  &:hover {
    background-color: #1b539c;
    border-left: 3px solid white;
    border-radius: 0 0.5rem 0.5rem 0;
  }
`;

const UserWrapper = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  background: linear-gradient(to right, #154077, #aabb00);
  border-bottom: 3px solid white;
  height: 8rem;
  margin-bottom: 1rem;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  text-align: left;
  margin-left: 1rem;
`;

const Img = styled.img`
  width: 2.5rem;
`;

const Name = styled.span<{ $header?: boolean }>`
  color: white;
  font-size: 1rem;
  font-weight: ${({ $header }) => ($header ? '700' : '500')};
  width: 100%;
  white-space: ${({ $header }) => ($header ? 'wrap' : 'nowrap')};
  overflow: hidden;
`;
